import { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { PageContainer } from '../../../../components/page-container/page-container'
import { Button } from '../../../../components/button'
import type { RootState } from '../../../../redux/global-store'
import {
  getJourneyRequest,
  concludeJourneyRequest,
  journeyReset,
} from '../../presentation/redux/actions/journey-actions'
import { paths } from '../../../../router/paths'
import { finishStyles, finishTexts } from './journey.styles'
import { showToast } from '../../../../components/sonner'

export const FinishJourney = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { journey, load, conclude, error } = useSelector(
    (state: RootState) => state.journey.journey,
  )
  const { user } = useSelector((state: RootState) => state.auth.login)

  // Buscar jornada atual caso ainda não esteja carregada
  useEffect(() => {
    if (!journey && user?.userId) {
      dispatch(getJourneyRequest({ userId: user.userId }))
    }
  }, [dispatch, journey, user])

  // Data atual formato es-GT (apenas data)
  const currentDate = useMemo(() => {
    return new Intl.DateTimeFormat('es-GT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date())
  }, [])

  const totalFormatted = useMemo(() => {
    if (!journey) return '--'
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ',
      minimumFractionDigits: 2,
    }).format(journey.totalAmount)
  }, [journey])

  const handleCancel = () => {
    navigate(paths.home)
  }

  // Navegar depois de concluído com sucesso
  useEffect(() => {
    if (conclude.success) {
      navigate(paths.journey.concluded)
    }
  }, [conclude.success, navigate])

  // Evitar exibir erro "herdado" na primeira montagem
  const firstRender = useRef(true)
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    if (!conclude.loading && error) {
      showToast(error, 'error')
    }
  }, [error, conclude.loading])

  // Limpar erro ao desmontar para não reaparecer quando voltar
  useEffect(() => {
    return () => {
      dispatch(journeyReset({ resetAll: false }))
    }
  }, [dispatch])

  const handleConfirm = () => {
    if (journey?.journeyId && !conclude.loading) {
      dispatch(concludeJourneyRequest(journey.journeyId))
    }
  }

  // Função para obter o texto do status
  const getStatusText = (status: number) => {
    switch (status) {
      case 1:
        return finishTexts.statusClosed
      case 0:
        return finishTexts.statusOpen
      case 2:
        return finishTexts.statusCancelled
      case 3:
        return finishTexts.statusInProgress
      case -1:
        return finishTexts.statusAll
      default:
        return status
    }
  }

  // Função para obter a classe do status do card
  const getCardStatusClass = (status: number) => {
    switch (status) {
      case 1:
        return finishStyles.cardStatusClosed
      case 0:
        return finishStyles.cardStatusOpen
      case 2:
        return finishStyles.cardStatusCancelled
      case 3:
        return finishStyles.cardStatusClosed // Usar closed como padrão para "Em Progresso"
      case -1:
        return finishStyles.cardStatusAll
      default:
        return finishStyles.cardStatusClosed
    }
  }

  return (
    <PageContainer type="authenticated" title={finishTexts.title}>
      <div className={finishStyles.container}>
        <div className={finishStyles.detailContainer}>
          <div className={finishStyles.card}>
            <div className={finishStyles.cardHeader}>
              <div className={finishStyles.cardDate}>
                <div className={finishStyles.cardRow}>
                  <span className={finishStyles.cardDateLabel}>
                    {finishTexts.resume}
                  </span>
                </div>
              </div>
            </div>
            <div className={finishStyles.cardBody}>
              {/* Data */}
              <div className={finishStyles.cardRow}>
                <span className={finishStyles.cardLabel}>
                  {finishTexts.dateLabel}
                </span>
                {!load && journey ? (
                  <span>{currentDate}</span>
                ) : (
                  <span className={finishStyles.skeletonLineSm} />
                )}
              </div>
              {/* Status */}
              <div className={finishStyles.cardRow}>
                <span className={finishStyles.cardLabel}>
                  {finishTexts.actualStatus}
                </span>
                {!load && journey ? (
                  <span
                    className={`${finishStyles.cardStatus} ${getCardStatusClass(journey.status)}`}
                  >
                    {getStatusText(journey.status)}
                  </span>
                ) : (
                  <span className={finishStyles.skeletonBadge} />
                )}
              </div>
              {/* Amount */}
              <div className={finishStyles.cardRow}>
                <span className={finishStyles.cardLabel}>
                  {finishTexts.amount}
                </span>
                {!load && journey ? (
                  <span className={finishStyles.cardAmount}>
                    {totalFormatted}
                  </span>
                ) : (
                  <span className={finishStyles.skeletonLineMd} />
                )}
              </div>
              {/* Route */}
              <div className={finishStyles.cardRow}>
                <span className={finishStyles.cardLabel}>
                  {finishTexts.routeLabel}
                </span>
                {!load && journey ? (
                  <span className={finishStyles.cardRoute}>
                    {journey.routeCode}
                  </span>
                ) : (
                  <span className={finishStyles.skeletonLineLg} />
                )}
              </div>
            </div>
          </div>

          {/* Mensagem quando não existe jornada depois do loading */}
          {!load && !journey && (
            <div className={finishStyles.emptyBox}>{finishTexts.noJourney}</div>
          )}
        </div>

        <div className={finishStyles.question}>{finishTexts.question}</div>

        <div className={finishStyles.buttonsWrapper}>
          <Button
            className={`${finishStyles.confirmButton}`}
            onClick={handleConfirm}
            disabled={load || !journey || conclude.loading}
          >
            {conclude.loading ? finishTexts.loading : finishTexts.confirm}
          </Button>
          <Button
            variant="outlined"
            className={`${finishStyles.cancelButton}`}
            onClick={handleCancel}
          >
            {finishTexts.abort}
          </Button>
        </div>
      </div>
    </PageContainer>
  )
}

export default FinishJourney
